import { statesByAbbrev, territoriesByAbbrev } from '@scoir/states'
import { addMonths, endOfMonth, endOfWeek, format, isValid, parse, startOfMonth, startOfWeek } from 'date-fns'

import { isValidTitle1Program } from './ExploreTable/types'
import { AvailabilityOption } from './components/FilterDrawer'
import {
  HSLocation,
  LOCALE_OPTIONS,
  LocaleOption,
  PercentageRange,
  RELIGIOUS_AFFILIATION_OPTIONS,
  ReligiousAffiliation,
  UIFilters,
  isValidSchoolType,
  unknownLocation,
} from './types'

type Converter<T extends UIFilters> = {
  [K in keyof T]: URLStateConverter<T[K]>
}

export const encodeToURLParams = <T extends Record<string, unknown>, C extends Converter<T>>(
  fields: T,
  converters: C,
  initialParams?: URLSearchParams,
): URLSearchParams => {
  const filterParams = new URLSearchParams(initialParams)
  Object.keys(converters).forEach((key) => {
    const k = key as keyof T
    const conv = converters[k] as URLStateConverter<T[keyof T]>
    if (fields?.[k] !== undefined) {
      const result = conv.encode(fields[k])
      if (result !== undefined) {
        filterParams.delete(key)
        if (Array.isArray(result)) {
          result.forEach((value) => {
            filterParams.append(key, value)
          })
        } else {
          filterParams.set(key, result)
        }
      } else {
        filterParams.delete(key)
      }
    } else {
      filterParams.delete(key)
    }
  })
  return filterParams
}

export const decodeFromURLParams = <T extends Record<string, unknown>, C extends Converter<T>>(
  params: URLSearchParams,
  converters: C,
) => {
  const state = {} as T
  Object.keys(converters).forEach((key) => {
    const k = key as keyof T
    const conv = converters[k] as URLStateConverter<T[keyof T]>
    const paramVal = params?.getAll(key)
    const decoded = conv.decode(paramVal)
    if (decoded !== undefined) {
      state[k] = decoded
    }
  })
  return state
}
export type URLStateConverter<T> = {
  encode: (v: T) => string | string[] | undefined
  decode: (v?: string[]) => T | undefined
}

// first provides a helper to work with multi-value query params, when only one is expected
const first =
  <T>(fn: (val?: string) => T) =>
  (v?: string[]) => {
    if (v === undefined) return
    return fn(v[0])
  }

const createPercentageRangeConverter = () => ({
  encode: (v: PercentageRange = {}) => {
    if (v.min === undefined && v.max === undefined) return
    return [v.min, v.max].map((i) => i ?? '').join(';')
  },
  decode: first((v?: string) => {
    if (!v) return
    const [min, max] = v.split(';').map((i) => {
      const n = parseInt(i, 10)
      return isNaN(n) ? undefined : n
    })
    if (min === undefined && max === undefined) return
    return { min, max }
  }),
})

export const UIFilterConverters: {
  [TKey in keyof UIFilters]-?: URLStateConverter<UIFilters[TKey]>
} = {
  schoolType: {
    encode: (v: UIFilters['schoolType']) => v,
    decode: first((v?: string) => {
      if (isValidSchoolType(v)) return v
      return
    }),
  },
  isActive: {
    encode: (v: UIFilters['isActive']) => (v?.toString() ? v?.toString() : undefined),
    decode: first((v?: string | boolean) => (isTrue(v) ? true : undefined)),
  },
  isTitle1Eligible: {
    encode: (v: UIFilters['isTitle1Eligible']) => (v != null ? v.toString() : undefined),
    decode: first((v?: string | boolean) => {
      if (v === undefined) return undefined
      if (isTrue(v)) return true
      if (isFalse(v)) return false
      return
    }),
  },
  virtual: {
    encode: (v: UIFilters['virtual']) => v?.toString(),
    decode: first((v?: string | boolean) => {
      if (v === undefined) return undefined
      if (isTrue(v)) return true
      if (isFalse(v)) return false
      return
    }),
  },
  collegeFair: {
    encode: (v: UIFilters['collegeFair']) => v?.toString(),
    decode: first((v?: string | boolean) => {
      if (v === undefined) return undefined
      if (isTrue(v)) return true
      if (isFalse(v)) return false
      return
    }),
  },
  title1Program: {
    encode: (v: UIFilters['title1Program']) => v,
    decode: first((v?: string) => {
      if (isValidTitle1Program(v)) return v
      return
    }),
  },
  classSize: {
    encode: (v: UIFilters['classSize'] = {}) => {
      if (v.min === undefined && v.max === undefined) return
      return [v.min, v.max].map((i) => i || '').join(';')
    },
    decode: first((v?: string) => {
      if (!v) return
      const [min, max] = v.split(';').map((i) => {
        const n = parseInt(i, 10)
        return isNaN(n) ? undefined : n
      })
      if (min === undefined && max === undefined) return
      return { min, max }
    }),
  },
  interestedHandraisers: {
    encode: (v: UIFilters['interestedHandraisers'] = {}) => {
      if (v.min === undefined && v.max === undefined) return
      return [v.min, v.max].map((i) => (i === undefined ? '' : i)).join(';')
    },
    decode: first((v?: string) => {
      if (!v) return
      const [min, max] = v.split(';').map((i) => {
        const n = parseInt(i, 10)
        return isNaN(n) ? undefined : n
      })
      if (min === undefined && max === undefined) return
      return { min, max }
    }),
  },
  interestedFollowers: {
    encode: (v: UIFilters['interestedFollowers'] = {}) => {
      if (v.min === undefined && v.max === undefined) return
      return [v.min, v.max].map((i) => (i === undefined ? '' : i)).join(';')
    },
    decode: first((v?: string) => {
      if (!v) return
      const [min, max] = v.split(';').map((i) => {
        const n = parseInt(i, 10)
        return isNaN(n) ? undefined : n
      })
      if (min === undefined && max === undefined) return
      return { min, max }
    }),
  },
  historicFollowers: {
    encode: (v: UIFilters['historicFollowers'] = {}) => {
      if (v.min === undefined && v.max === undefined) return
      return [v.min, v.max].map((i) => (i === undefined ? '' : i)).join(';')
    },
    decode: first((v?: string) => {
      if (!v) return
      const [min, max] = v.split(';').map((i) => {
        const n = parseInt(i, 10)
        return isNaN(n) ? undefined : n
      })
      if (min === undefined && max === undefined) return
      return { min, max }
    }),
  },
  visitRange: {
    encode: (v: UIFilters['visitRange']) => {
      if (v?.type === 'custom') {
        return [v.startDate, v.endDate]
          .map((d) => {
            if (!d) return ''
            return format(d, 'yyyy-MM-dd')
          })
          .join(';')
      }
      return v?.type
    },
    decode: first((val?: string) => {
      if (!val) return
      if (val === 'this-week' || val === 'this-month' || val === 'next-month') {
        return { type: val, ...getVisitVailabilityFromOption(val) }
      }
      const [startDate, endDate] = val.split(';').map((i) => {
        const d = parse(i, 'yyyy-MM-dd', new Date())
        return isValid(d) ? d : undefined
      })
      if (startDate === undefined && endDate === undefined) return
      return { type: 'custom', startDate, endDate }
    }),
  },
  pctFreeReducedLunch: createPercentageRangeConverter(),
  pctFirstGenerationStudents: createPercentageRangeConverter(),
  pctAttendTwoYearCollegeStudents: createPercentageRangeConverter(),
  pctAttendFourYearCollegeStudents: createPercentageRangeConverter(),
  pctWhiteCaucasianStudents: createPercentageRangeConverter(),
  pctBlackAfricanAmericanStudents: createPercentageRangeConverter(),
  pctHispanicLatinoStudents: createPercentageRangeConverter(),
  pctAsianStudents: createPercentageRangeConverter(),
  pctNativeAmericanStudents: createPercentageRangeConverter(),
  pctOtherUndisclosedStudents: createPercentageRangeConverter(),
  religiousAffiliation: {
    encode: (v: UIFilters['religiousAffiliation']) => (!v || v.length === 0 ? undefined : v),
    decode: (v?: string[]) => {
      if (!v || v.length === 0) return undefined
      return v.reduce((acc, affiliation) => {
        if (RELIGIOUS_AFFILIATION_OPTIONS.includes(affiliation as ReligiousAffiliation)) {
          acc.push(affiliation as ReligiousAffiliation)
        }
        return acc
      }, [] as ReligiousAffiliation[])
    },
  },
  text: {
    encode: (v: UIFilters['text']) => (v === '' ? undefined : v),
    decode: first((v?: string) => (v === '' ? undefined : v)),
  },
  locations: {
    encode: (v: UIFilters['locations']) => (!v || v.length === 0 ? undefined : v),
    decode: (v?: string[]) => {
      if (!v || v.length === 0) return undefined
      return v.reduce((acc, location) => {
        const state = statesByAbbrev[location as keyof typeof statesByAbbrev]
        const territory = territoriesByAbbrev[location as keyof typeof territoriesByAbbrev]
        if (state) acc.push(state.value as HSLocation)
        if (territory) acc.push(territory.value as HSLocation)
        if (location === unknownLocation) acc.push('')
        return acc
      }, [] as HSLocation[])
    },
  },
  locale: {
    encode: (v: UIFilters['locale']) => (!v || v.length === 0 ? undefined : v),
    decode: (v?: string[]) => {
      if (!v || v.length === 0) return undefined
      return v.reduce((acc, locale) => {
        if (LOCALE_OPTIONS.includes(locale as LocaleOption)) {
          acc.push(locale as LocaleOption)
        }
        return acc
      }, [] as LocaleOption[])
    },
  },
  cities: {
    encode: (v: UIFilters['cities']) => (!v || v.length === 0 ? undefined : v),
    decode: (v?: string[]) => {
      if (!v || v.length === 0) return undefined
      return v
    },
  },
  zipCodes: {
    encode: (v: UIFilters['zipCodes']) => (!v || v.length === 0 ? undefined : v),
    decode: (v?: string[]) => {
      if (!v || v.length === 0) return undefined
      return v
    },
  },
  hasCounselorSuggestions: {
    encode: (v: UIFilters['hasCounselorSuggestions']) => (v != null ? v.toString() : undefined),
    decode: first((v?: string | boolean) => {
      if (v === undefined) return undefined
      if (isTrue(v)) return true
      if (isFalse(v)) return false
      return
    }),
  },
  geomarkets: {
    encode: (v: UIFilters['geomarkets']) => (!v || v.length === 0 ? undefined : v),
    decode: (v?: string[]) => {
      if (!v || v.length === 0) return undefined
      return v
    },
  },
  isTarget: {
    encode: (v: UIFilters['isTarget']) => (v?.toString() ? v?.toString() : undefined),
    decode: first((v?: string | boolean) => (isTrue(v) ? true : undefined)),
  },
} as const

function getVisitVailabilityFromOption(option: AvailabilityOption | undefined): UIFilters['visitRange'] {
  const now = new Date()
  switch (option) {
    case 'this-week': {
      const startDate = startOfWeek(now)
      const endDate = endOfWeek(now)
      return { type: option, startDate, endDate }
    }
    case 'this-month': {
      const startDate = startOfMonth(now)
      const endDate = endOfMonth(now)
      return { type: option, startDate, endDate }
    }
    case 'next-month': {
      const nextMonth = addMonths(now, 1)
      const startDate = startOfMonth(nextMonth)
      const endDate = endOfMonth(nextMonth)
      return { type: option, startDate, endDate }
    }
    case 'custom':
      return { type: option }
    default:
      return undefined
  }
}
const isTrue = (v?: string | boolean) => v === true || (typeof v === 'string' && v.toLowerCase() === 'true')
const isFalse = (v?: string | boolean) => v === false || (typeof v === 'string' && v.toLowerCase() === 'false')
